import axiosInstance from "../../helpers/axios";
import { API_URL as APK_API_URL } from "../../helpers/utils";
import { catchHandler, dispatchError, dispatchLoading, elseHandler, Reset } from "../../helpers/dispatch";
import { requestPayloadBodyFormate, responseDataFormate } from "../../helpers/bycrypt";
import { AppDispatch } from "../../store";
import { Network } from "@capacitor/network";
import { message } from "antd";

export const MatchFaceSignatureAPI =
  (image: string, onSuccess: (data: any) => void, onError: (error: any) => void) =>
    async (dispatch: AppDispatch): Promise<void> => {
      dispatchLoading(dispatch, "matchFace", true);
      const formatedData = requestPayloadBodyFormate({ image });
      axiosInstance
        .post(`${APK_API_URL}/employee-attendance/match-face`, formatedData)
        .then((response) => {
          let result = responseDataFormate(response.data);
          if (result?.success) {
            dispatchError(dispatch, "matchFace", undefined);
            onSuccess(result.data);
          } else {
            elseHandler(dispatch, "matchFace", result);
            onError(new Error(result?.message || "Face not recognized."));
          }
          dispatchLoading(dispatch, "matchFace", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "matchFace")(e);
          dispatchLoading(dispatch, "matchFace", false);

          let errorMsg = e.message;
          if (e.response && e.response.data) {
            try {
              const parsed = typeof e.response.data === "string" ? responseDataFormate(e.response.data) : e.response.data;
              if (parsed && parsed.message) {
                errorMsg = parsed.message;
              }
            } catch (err) { }
          }
          onError(new Error(errorMsg));
        });
    };

export const AddEmployeeAttendanceAPI =
  (data: any, onSuccess: () => void, onError: (error: any) => void) =>
    async (dispatch: AppDispatch): Promise<void> => {
      dispatchLoading(dispatch, "addAttendance", true);
      const formatedData = requestPayloadBodyFormate(data);
      axiosInstance
        .post(`${APK_API_URL}/employee-attendance`, formatedData)
        .then((response) => {
          let result = responseDataFormate(response.data);
          if (result?.success) {
            dispatchError(dispatch, "addAttendance", undefined);
            onSuccess();
          } else {
            elseHandler(dispatch, "addAttendance", result);
            onError(new Error(result?.message || "Failed to record attendance."));
          }
          dispatchLoading(dispatch, "addAttendance", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "addAttendance")(e);
          dispatchLoading(dispatch, "addAttendance", false);
          onError(e);
        });
    };

export const EditEmployeeAttendanceAPI =
  (id: string, data: { clockInTime?: string; clockOutTime?: string }, onSuccess: () => void, onError: (error: any) => void) =>
    async (dispatch: AppDispatch): Promise<void> => {
      dispatchLoading(dispatch, "editAttendance", true);
      const formatedData = requestPayloadBodyFormate(data);
      axiosInstance
        .put(`${APK_API_URL}/employee-attendance/edit/${id}`, formatedData)
        .then((response) => {
          let result = responseDataFormate(response.data);
          if (result?.success) {
            dispatchError(dispatch, "editAttendance", undefined);
            onSuccess();
          } else {
            elseHandler(dispatch, "editAttendance", result);
            onError(new Error(result?.message || "Failed to edit attendance."));
          }
          dispatchLoading(dispatch, "editAttendance", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "editAttendance")(e);
          dispatchLoading(dispatch, "editAttendance", false);
          onError(e);
        });
    };

export const SyncEmployeeDataAPI =
  ({
    filters,
    onSuccess,
    onError,
    signal,
  }: {
    filters: {
      page?: number;
      limit?: number;
      last_sync_date?: string | null;
      user?: string;
    };
    onSuccess: (data: any) => void;
    onError: (error: any) => void;
    signal?: AbortSignal;
  }) =>
    async (dispatch: AppDispatch): Promise<void> => {
      const networkStatus = await Network.getStatus();
      if (!networkStatus.connected) {
        onError(new Error("No internet connection. Please check your network and try again."));
        return;
      }
      dispatchLoading(dispatch, "syncEmployeeData", true);

      // Construct query params
      const queryParams = new URLSearchParams();
      if (filters.page) queryParams.append("page", filters.page.toString());
      if (filters.limit) queryParams.append("limit", filters.limit.toString());
      if (filters.last_sync_date) queryParams.append("last_sync_date", filters.last_sync_date);
      if (filters.user) queryParams.append("user", filters.user);

      axiosInstance
        .get(`${APK_API_URL}/users/sync-data?${queryParams.toString()}`, { signal })
        .then((response) => {
          let result = responseDataFormate(response.data);
          if (result?.success) {
            dispatchError(dispatch, "syncEmployeeData", undefined);
            onSuccess(result.data);
          } else {
            elseHandler(dispatch, "syncEmployeeData", result);
            onError(new Error(result?.message || "Failed to sync employee data."));
          }
          dispatchLoading(dispatch, "syncEmployeeData", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "syncEmployeeData")(e);
          dispatchLoading(dispatch, "syncEmployeeData", false);
          onError(e);
        });
    };

export const GetEmployeeAttendanceListAPI =
  (
    params: { start?: string; end?: string; user?: string; flag?: string; department_id?: string; auto_clock_out_status?: string },
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) =>
    async (dispatch: AppDispatch): Promise<void> => {
      dispatchLoading(dispatch, "getEmployeeAttendanceList", true);

      const queryParams = new URLSearchParams();
      if (params.start) queryParams.append("start", params.start);
      if (params.end) queryParams.append("end", params.end);
      if (params.user) queryParams.append("user", params.user);
      if (params.flag) queryParams.append("flag", params.flag);
      if (params.department_id) queryParams.append("department_id", params.department_id);
      if (params.auto_clock_out_status) queryParams.append("auto_clock_out_status", params.auto_clock_out_status);

      axiosInstance
        .get(`${APK_API_URL}/employee-attendance?${queryParams.toString()}`)
        .then((response) => {
          let result = responseDataFormate(response.data);
          if (result?.success) {
            dispatch({
              type: "GET_EMPLOYEE_ATTENDANCE_LIST",
              payload: result?.data,
            });
            dispatchError(dispatch, "getEmployeeAttendanceList", undefined);
            if (onSuccess) onSuccess(result.data);
          } else {
            elseHandler(dispatch, "getEmployeeAttendanceList", result);
            if (onError) onError(new Error(result?.message || "Failed to fetch attendance list."));
          }
          dispatchLoading(dispatch, "getEmployeeAttendanceList", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "getEmployeeAttendanceList")(e);
          dispatchLoading(dispatch, "getEmployeeAttendanceList", false);
          if (onError) onError(e);
        });
    };

export const BulkAddEmployeeAttendanceAPI =
  (data: any[], onSuccess: (result: any) => void, onError: (error: any) => void) =>
    async (dispatch: AppDispatch): Promise<void> => {
      const networkStatus = await Network.getStatus();
      console.log("calling networkStatus BulkAddEmployeeAttendanceAPI", networkStatus)
      if (!networkStatus.connected) {
        onError(new Error("No internet connection. Please check your network and try again."));
        return;
      }
      dispatchLoading(dispatch, "addAttendance", true);

      // We do not format the entire array through requestPayloadBodyFormate because it expects an object.
      // Assuming data is an array of already formatted payload objects (or objects we can JSON stringify securely)
      // Wait, the backend requires encryption. requestPayloadBodyFormate encrypts it.
      // So we encrypt the entire array. requestPayloadBodyFormate stringifies the input and encrypts it!
      const formatedData = requestPayloadBodyFormate(data);

      axiosInstance
        .post(`${APK_API_URL}/employee-attendance/bulk-offline`, formatedData)
        .then((response) => {
          let result = responseDataFormate(response.data);
          if (result?.success) {
            dispatchError(dispatch, "addAttendance", undefined);
            onSuccess(result.data);
          } else {
            dispatchError(dispatch, "addAttendance", result?.message || result?.error || "Failed to add bulk attendance");
            onError(new Error(result?.message || "Failed to add bulk attendance"));
          }
          dispatchLoading(dispatch, "addAttendance", false);
        })
        .catch((e) => {
          dispatchLoading(dispatch, "addAttendance", false);
          if (e?.response?.status === 444) {
            Reset();
            return;
          }
          let errorMsg = e.message;
          if (e.response && e.response.data) {
            try {
              const parsed = typeof e.response.data === "string" ? responseDataFormate(e.response.data) : e.response.data;
              if (parsed && parsed.message) errorMsg = parsed.message;
            } catch (err) { }
          }
          dispatchError(dispatch, "addAttendance", [errorMsg]);
          const errObj: any = new Error(errorMsg);
          errObj.isAxiosError = e.isAxiosError;
          errObj.response = e.response;
          onError(errObj);
        });
    };

export const GetPendingLogsAPI =
  (
    params: { start?: string; end?: string; startDate?: string; endDate?: string; search?: string; page?: number; limit?: number; payrollFilter?: string; employeeFilter?: string; issueFilter?: string; statusFilter?: string },
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) =>
    async (dispatch: AppDispatch): Promise<void> => {
      try {
        const queryParams = new URLSearchParams();
        const activeStart = params.startDate || params.start;
        const activeEnd = params.endDate || params.end;

        if (activeStart) queryParams.append("startDate", activeStart);
        if (activeEnd) queryParams.append("endDate", activeEnd);
        if (params.search) queryParams.append("search", params.search);
        if (params.page) queryParams.append("page", params.page.toString());
        if (params.limit) queryParams.append("limit", params.limit.toString());
        if (params.payrollFilter) queryParams.append("payrollFilter", params.payrollFilter);
        if (params.employeeFilter) queryParams.append("employeeFilter", params.employeeFilter);
        if (params.issueFilter) queryParams.append("issueFilter", params.issueFilter);
        if (params.statusFilter) queryParams.append("statusFilter", params.statusFilter);

        const response = await axiosInstance.get(`${APK_API_URL}/employee-attendance/pending-logs?${queryParams.toString()}`);
        let result = responseDataFormate(response.data);

        if (result?.success) {
          if (onSuccess) onSuccess(result.data);
        } else {
          message.error(result?.message || "Failed to fetch pending logs.");
          if (onError) onError(new Error(result?.message || "Failed to fetch pending logs."));
        }
      } catch (e: any) {
        if (e.code === "ERR_CANCELED" || e.name === "AbortError") return;
        console.error("Get Pending Logs Error:", e);
        message.error(e?.response?.data?.message || "Unable to fetch pending logs");
        if (onError) onError(e);
      }
    };

export const GetPendingLogsCountAPI =
  (
    params: { start?: string; end?: string; startDate?: string; endDate?: string },
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) =>
    async (dispatch: AppDispatch): Promise<void> => {
      dispatchLoading(dispatch, "getPendingLogsCount", true);

      const queryParams = new URLSearchParams();
      const activeStart = params.startDate || params.start;
      const activeEnd = params.endDate || params.end;

      if (activeStart) queryParams.append("startDate", activeStart);
      if (activeEnd) queryParams.append("endDate", activeEnd);
      queryParams.append("countOnly", "true");

      axiosInstance
        .get(`${APK_API_URL}/employee-attendance/pending-logs?${queryParams.toString()}`)
        .then((response) => {
          let result = responseDataFormate(response.data);
          if (result?.success) {
            dispatchError(dispatch, "getPendingLogsCount", undefined);
            if (onSuccess) onSuccess(result.data?.count || 0);
          } else {
            elseHandler(dispatch, "getPendingLogsCount", result);
            if (onError) onError(new Error(result?.message || "Failed to fetch pending logs count."));
          }
          dispatchLoading(dispatch, "getPendingLogsCount", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "getPendingLogsCount")(e);
          dispatchLoading(dispatch, "getPendingLogsCount", false);
          if (onError) onError(e);
        });
    };

export const FixEmployeeLogAPI =
  (data: any, onSuccess: (res: any) => void, onError: (error: any) => void) =>
    async (dispatch: AppDispatch): Promise<void> => {
      dispatchLoading(dispatch, "fixLog", true);
      const formatedData = requestPayloadBodyFormate(data);
      axiosInstance
        .post(`${APK_API_URL}/employee-attendance/fix-log`, formatedData)
        .then((response) => {
          let result = responseDataFormate(response.data);
          if (result?.success) {
            dispatchError(dispatch, "fixLog", undefined);
            onSuccess(result.data);
          } else {
            elseHandler(dispatch, "fixLog", result);
            onError(new Error(result?.message || "Failed to fix log."));
          }
          dispatchLoading(dispatch, "fixLog", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "fixLog")(e);
          dispatchLoading(dispatch, "fixLog", false);

          let errorMsg = e.message;
          if (e.response && e.response.data) {
            try {
              const parsed = typeof e.response.data === "string" ? responseDataFormate(e.response.data) : e.response.data;
              if (parsed && parsed.message) {
                errorMsg = parsed.message;
              }
            } catch (err) { }
          }
          onError(new Error(errorMsg));
        });
    };

export const EditAutoClockoutTimeAPI =
  (data: any, onSuccess: any, onError: any) =>
    async (dispatch: AppDispatch): Promise<void> => {
      dispatchLoading(dispatch, "editAttendance", true);

      const formatedData = requestPayloadBodyFormate(data);

      axiosInstance
        .put(`${APK_API_URL}/employee-attendance/auto-clock-out`, formatedData)
        .then((response) => {
          let result = responseDataFormate(response.data);

          if (result?.success) {
            dispatchError(dispatch, "editAttendance", undefined);
            onSuccess();
          } else {
            elseHandler(dispatch, "editAttendance", result);
            onError(new Error(result?.message || "Failed to edit auto clock-out time"));
          }
          dispatchLoading(dispatch, "editAttendance", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "editAttendance")(e);
          dispatchLoading(dispatch, "editAttendance", false);
          let errorMsg = e.message;
          if (e.response && e.response.data) {
            try {
              const parsed = typeof e.response.data === "string" ? responseDataFormate(e.response.data) : e.response.data;
              if (parsed && parsed.message) errorMsg = parsed.message;
            } catch (err) { }
          }
          onError(new Error(errorMsg));
        });
    };

export const EditPendingLogTimeAPI =
  (data: any, onSuccess: any, onError: any) =>
    async (dispatch: AppDispatch): Promise<void> => {
      dispatchLoading(dispatch, "editAttendance", true);

      const formatedData = requestPayloadBodyFormate(data);

      axiosInstance
        .put(`${APK_API_URL}/employee-attendance/pending-log-time`, formatedData)
        .then((response) => {
          let result = responseDataFormate(response.data);

          if (result?.success) {
            dispatchError(dispatch, "editAttendance", undefined);
            onSuccess();
          } else {
            elseHandler(dispatch, "editAttendance", result);
            onError(new Error(result?.message || "Failed to edit pending log time"));
          }
          dispatchLoading(dispatch, "editAttendance", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "editAttendance")(e);
          dispatchLoading(dispatch, "editAttendance", false);
          let errorMsg = e.message;
          if (e.response && e.response.data) {
            try {
              const parsed = typeof e.response.data === "string" ? responseDataFormate(e.response.data) : e.response.data;
              if (parsed && parsed.message) errorMsg = parsed.message;
            } catch (err) { }
          }
          onError(new Error(errorMsg));
        });
    };

export const GetAutoClockoutPendingCountAPI =
  () =>
    async (dispatch: (action: any) => void) => {
      dispatchLoading(dispatch, "getAutoClockoutPendingCount", true);
      return new Promise((resolve) => {
        axiosInstance
          .get(`/employee-attendance/auto-clockout-pending-count`)
          .then((response) => {
            let result = responseDataFormate(response?.data);
            if (result?.success) {
              resolve(result.data?.count || 0);
            } else {
              elseHandler(dispatch, "getAutoClockoutPendingCount", result);
              resolve(0);
            }
            dispatchLoading(dispatch, "getAutoClockoutPendingCount", false);
          })
          .catch((err) => {
            catchHandler(dispatch, "getAutoClockoutPendingCount")(err);
            resolve(0);
          });
      });
    };

export const EditGroupAttendanceTimeAPI =
  (data: any, onSuccess: any, onError: any) =>
    async (dispatch: AppDispatch): Promise<void> => {
      dispatchLoading(dispatch, "editAttendance", true);

      const formatedData = requestPayloadBodyFormate(data);

      axiosInstance
        .put(`${APK_API_URL}/employee-attendance/modify-group-time`, formatedData)
        .then((response) => {
          let result = responseDataFormate(response.data);

          if (result?.success) {
            dispatchError(dispatch, "editAttendance", undefined);
            onSuccess();
          } else {
            elseHandler(dispatch, "editAttendance", result);
            onError(new Error(result?.message || "Failed to edit group attendance time"));
          }
          dispatchLoading(dispatch, "editAttendance", false);
        })
        .catch((e) => {
          catchHandler(dispatch, "editAttendance")(e);
          dispatchLoading(dispatch, "editAttendance", false);
          onError(e);
        });
    };
